import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  ViewEncapsulation,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgTemplateOutlet } from '@angular/common';
import { MatButton } from '@angular/material/button';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { AvailableLangs, LangDefinition, TranslocoService } from '@jsverse/transloco';

const LANG_STORAGE_KEY = 'lang';

/** รหัสประเทศของธงตามภาษา (ไฟล์อยู่ที่ public/images/flags) */
const FLAG_CODES: Record<string, string> = {
  th: 'th',
  en: 'us',
};

/**
 * ปุ่มเลือกภาษาแบบมีธง นำมาจาก ward-vettale (ต้นทางคือ layout/common/languages ของเว็บหลัก)
 * ไม่มีส่วนอัปเดตเมนู Fuse navigation เพราะ patient-dentapop ไม่มีเมนูนำทาง
 */
@Component({
  selector: 'app-languages',
  templateUrl: './languages.html',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  exportAs: 'languages',
  imports: [MatButton, MatMenuTrigger, NgTemplateOutlet, MatMenu, MatMenuItem],
})
export class Languages implements OnInit {
  private readonly transloco = inject(TranslocoService);
  private readonly destroyRef = inject(DestroyRef);

  availableLangs: LangDefinition[] = [];
  readonly activeLang = signal<string>('');

  ngOnInit(): void {
    this.availableLangs = normalizeAvailableLangs(this.transloco.getAvailableLangs());

    this.transloco.langChanges$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((lang) => this.activeLang.set(lang));

    // คืนค่าภาษาที่ผู้ใช้เคยเลือกไว้ในเครื่องนี้ (ค่าไม่รู้จักให้ข้าม)
    const stored = readStoredLang();
    if (
      stored &&
      stored !== this.transloco.getActiveLang() &&
      this.availableLangs.some((lang) => lang.id === stored)
    ) {
      this.transloco.setActiveLang(stored);
    }
  }

  setActiveLang(lang: string): void {
    this.transloco.setActiveLang(lang);
    try {
      localStorage.setItem(LANG_STORAGE_KEY, lang);
    } catch {
      // บางเบราว์เซอร์ปิด storage (เช่น private mode) การเปลี่ยนภาษายังทำงานได้ตามปกติ
    }
  }

  flagUrl(lang: string): string {
    return `images/flags/${(FLAG_CODES[lang] ?? lang).toUpperCase()}.svg`;
  }
}

function normalizeAvailableLangs(availableLangs: AvailableLangs): LangDefinition[] {
  return availableLangs.map((lang) =>
    typeof lang === 'string' ? { id: lang, label: lang } : lang,
  );
}

function readStoredLang(): string | null {
  try {
    return localStorage.getItem(LANG_STORAGE_KEY);
  } catch {
    return null;
  }
}
